import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {connect} from 'react-redux';
import {Alert, Form, Select, Space} from 'antd';
import {Collapse} from 'react-collapse';
import InvoicePreview from '../InvoicePreview';
import Spin from '../../../Spin';
import {useAccountingOptions} from '../../../../hooks';
import {formHelpers, objectHelpers} from '../../../../utils/helpers';
import {validation} from '../../../../utils/validation';
import {accountingActions} from '../../../../state/actions/accounting.actions';

const {Item} = Form;

const formFieldProps = { size: 'large' };

const initialFormValues = {
  department: undefined,
  list_item: undefined,
  location: undefined,
  tax_code: undefined
};

const UpdateBillForm = React.forwardRef(({
  dispatch,
  createInvoice,
  syncInvoice,
  updateInvoice,

  entry,
  onSuccess,
  setSubmitButtonProps,
  ...rest
}, ref) => {
  const [t] = useTranslation(['main', 'accounting']);
  const [form] = Form.useForm();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const {
    departmentOptions,
    listItemOptions,
    locationOptions,
    taxCodeOptions
  } = useAccountingOptions();

  const requiredItemProps = useMemo(() => ({rules: validation.required(t), required: true}), [t]);

  const trans = (key) => t(`accounting:${key}`);

  const formT = (key) => trans(`modal.updateBill.${key}`);

  const placeholder = (key) => formT(`placeholder.${key}`);

  useEffect(() => {
    form.resetFields();
    error && setError(null);
  }, [entry, form]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatFormValues = (fields, variant) => {
    const values = objectHelpers.trimAllValues(fields);
    const { department, list_item, location, tax_code } = values;
    const amount = parseFloat(entry?.lines?.[0]?.line_debit) || parseFloat(entry?.lines?.[0]?.line_credit) || 0;
    const memo = entry.memo;
    const commonFields = { memo, amount: String(amount), tax_code };

    let data = {
      department,
      location,
      memo,
      tax_code,
      item: [{ item: list_item, description: memo, quantity: '1', rate: amount, ...commonFields }],
      entity: entry?.vendor.accounting_external_reference,
    };

    if (variant === 'create') {
      data = {
        ...data,
        item: [{ item: list_item, description: memo, quantity: '1', rate: amount, ...commonFields }],
        expense: [{ account: undefined, ...commonFields }],
        expense_id: entry?.expense_id,
        exchange_rate: '1',
        transaction_date: entry.transaction_date,
        vendor_id: entry.vendor_local_id,
      };
    } else {
      data = {
        ...data,
        item: [{ item: list_item }],
      }
    }

    return data;
  };

  const handleSubmit = (fields) => {
    setLoading(true);
    setSubmitButtonProps({loading: true});

    const id = entry?.vendor_bill_id;

    const invoiceAction = id ? updateInvoice : createInvoice;

    const errorCallback = (response) => {
      formHelpers.handleFormErrors({ form, response, t, setError });
      setLoading(false);
      setSubmitButtonProps({ loading: false });
    }

    invoiceAction?.({
      ...(id && { id }),
      data: formatFormValues(fields, id ? 'update' : 'create'),
      successCallback: (invoice) => {
        syncInvoice?.({
          id: invoice.id,
          successCallback: (response) => {
            setLoading(false);
            setSubmitButtonProps({ loading: false });
            onSuccess?.(response);
            form.resetFields();
          },
          errorCallback
        })
      },
      errorCallback
    });
  }

  return (
    <Space direction='vertical' style={{ width: '100%' }} size='large'>

      {entry?.invoice && <InvoicePreview invoice={entry.invoice} />}

      <Spin spinning={loading} size='small'>
        <Form
          {...rest}
          initialValues={initialFormValues}
          onFinish={handleSubmit}
          layout='vertical'
          form={form}
          ref={ref}
        >
          <Item
            {...requiredItemProps}
            label={t('taxCode')}
            name='tax_code'
          >
            <Select
              {...formFieldProps}
              placeholder={placeholder('taxCode')}
              options={taxCodeOptions}
            />
          </Item>
          <Item
            {...requiredItemProps}
            label={t('department')}
            name='department'
          >
            <Select
              {...formFieldProps}
              placeholder={placeholder('department')}
              options={departmentOptions}
            />
          </Item>
          <Item
            label={t('location')}
            name='location'
          >
            <Select
              {...formFieldProps}
              placeholder={placeholder('location')}
              options={locationOptions}
            />
          </Item>
          <Item
            {...requiredItemProps}
            label={t('listItem')}
            name='list_item'
          >
            <Select
              {...formFieldProps}
              placeholder={placeholder('listItem')}
              options={listItemOptions}
            />
          </Item>

          <input
            className='d-none'
            disabled={loading}
            onClick={() => form.submit()}
            type='submit'
          />

          <Collapse isOpened={Boolean(error)}>
            <Alert message={error} type='error' showIcon />
          </Collapse>
        </Form>
      </Spin>

    </Space>
  );
});

UpdateBillForm.propTypes = {
  entry: PropTypes.object,
  onSuccess: PropTypes.func,
  setSubmitButtonProps: PropTypes.func,
}

const mapDispatchToProps = {
  createInvoice: accountingActions.createInvoice,
  syncInvoice: accountingActions.syncInvoice,
  updateInvoice: accountingActions.updateInvoice,
};

export default connect(null, mapDispatchToProps, null, {forwardRef: true})(UpdateBillForm);
